import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { fetchChatHistoryByUser } from '../slice/api/historySlice';
import { parseISO, format } from 'date-fns';
import constants from '../components/constants';
import { Trash2Icon } from 'lucide-react';
import { deleteHistory, updateHistoryName } from '../Api/req';
import Loader from '../components/atomic/Loader';
import DeleteModal from '../components/atomic/DeleteModal';
import { fetchSQByTaskId, selectSQ } from '../slice/sqSlice';
import { getTaskStatusesByHistoryAsync } from '../slice/taskStatusSlice';

const CircularProgress = ({ completed, total }) => {
  const radius = 10;
  const circumference = 2 * Math.PI * radius;
  const percentage = (completed || 0) / (total || 1);
  const fillLength = circumference * percentage;
  const strokeDasharray = `${fillLength} ${circumference}`;

  return (
    <div className="flex items-center gap-2">
      <svg className="w-7 h-7 transform -rotate-90">
        <circle
          cx="12"
          cy="12"
          r={radius}
          stroke="#E5E7EB"
          strokeWidth="2.5"
          fill="none"
        />
        <circle
          cx="12"
          cy="12"
          r={radius}
          stroke="#5F6368"
          strokeWidth="2.5"
          fill="none"
          strokeDasharray={strokeDasharray}
          strokeLinecap="round"
          style={{ transition: 'stroke-dasharray 0.5s ease' }}
        />
      </svg>
      <span className="text-xs font-medium text-gray-600">
        {completed || 0}/{total || 0}
      </span>
      <h1 className="text-xs font-medium text-gray-600">Beantwortet</h1>
    </div>
  );
};

const History = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { chatHistory = [], status, error } = useSelector((state) => state.history);
  const [deletingIds, setDeletingIds] = useState([]);
  const [selectedChatId, setSelectedChatId] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [sortKey, setSortKey] = useState('timestamp');
  const [sortOrder, setSortOrder] = useState('desc');
  const [editingId, setEditingId] = useState(null);
  const [editingTitle, setEditingTitle] = useState('');
  const taskStatuses = useSelector((state) => state.taskStatus.taskStatuses);
  const questions = useSelector(selectSQ);
  const { data: userData } = useSelector((state) => state.admin);
  const handleSort = (key) => {
    if (sortKey === key) {
      setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortOrder('asc');
    }
  };
  // Track questions and statuses for each history
  const [historyData, setHistoryData] = useState(new Map());

  const user_id = localStorage.getItem('id');
  
  // Fetch chat history
  useEffect(() => {
    if (user_id) {
      dispatch(fetchChatHistoryByUser(user_id));
    }
  }, [dispatch, user_id]);
  
  // Fetch data for each history item
  useEffect(() => {
    if (chatHistory?.length > 0) {
      // Clear history data when chat history changes
      setHistoryData(new Map());

      chatHistory.forEach(record => {
        if (record.task_id && record.id) {
          // Fetch questions for this task
          dispatch(fetchSQByTaskId(record.task_id))
            .then((action) => {
              if (action.payload) {
                setHistoryData(prev => {
                  const newMap = new Map(prev);
                  const currentData = newMap.get(record.id) || {};
                  newMap.set(record.id, {
                    ...currentData,
                    questions: action.payload.questions || []
                  });
                  return newMap;
                });
              }
            });

          // Fetch task statuses for this history
          dispatch(getTaskStatusesByHistoryAsync(record.id))
            .then((action) => {
              if (action.payload) {
                setHistoryData(prev => {
                  const newMap = new Map(prev);
                  const currentData = newMap.get(record.id) || {};
                  newMap.set(record.id, {
                    ...currentData,
                    statuses: action.payload || []
                  });
                  return newMap;
                });
              }
            });
        }
      });
    }
  }, [chatHistory, dispatch]);

  if (status === 'loading') {
    return <Loader size='lg' color='gray'/>;
  }

  if (status === 'failed') {
    return <div>Error: {error}</div>;
  }

  const formatTimestamp = (timestamp) => {
    try {
      const date = parseISO(timestamp);
      return format(date, 'dd MMM, yyyy');
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const getCompletedQuestions = (historyId) => {
    const data = historyData.get(historyId) || {};
    const questions = data.questions || [];
    const statuses = data.statuses || [];
    
    const completedCount = statuses.filter(ts => ts.status === true).length;

    console.log('Progress for history:', historyId, {
      totalQuestions: questions.length,
      completedCount
    });

    return {
      completed: completedCount,
      total: questions.length || 0
    };
  };

  const handleViewChat = (id) => {
    // Check if user has "No License" role
    if (userData?.user_role === 'No License') {
      navigate('/hilfe');
      return;
    }
    navigate(`/analysis/${id}`);
  };

  const handleDeleteHistory = async (id) => {
    try {
      setDeletingIds(prev => [...prev, id]);
      await deleteHistory(id);
      dispatch(fetchChatHistoryByUser(user_id));
    } catch (error) {
      console.error('Failed to delete history:', error);
    } finally {
      setDeletingIds(prev => prev.filter(deleteId => deleteId !== id));
    }
  };

  const handleDeleteClick = (id) => {
    setSelectedChatId(id);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (selectedChatId) {
      await handleDeleteHistory(selectedChatId);
      setIsDeleteModalOpen(false);
      setSelectedChatId(null);
    }
  };

  const handleEditClick = (id, currentTitle) => {
    setEditingId(id);
    setEditingTitle(currentTitle);
  };

  const handleTitleChange = (e) => {
    setEditingTitle(e.target.value);
  };

  const handleTitleSubmit = async (id) => {
    try {
      await updateHistoryName(id, editingTitle);
      dispatch(fetchChatHistoryByUser(user_id));
      setEditingId(null);
      setEditingTitle('');
    } catch (error) {
      console.error('Failed to update history name:', error);
    }
  };

  const handleTitleKeyPress = (e, id) => {
    if (e.key === 'Escape') {
      setEditingId(null);
      setEditingTitle('');
    }
  };

  // Ensure chatHistory is always an array and sort it
  const history = Array.isArray(chatHistory) ? [...chatHistory].sort((a, b) => {
    if (sortKey === 'title') {
      const titleA = (a.history_name || a.task_name || '').toLowerCase();
      const titleB = (b.history_name || b.task_name || '').toLowerCase();
      return sortOrder === 'asc' ? titleA.localeCompare(titleB) : titleB.localeCompare(titleA);
    } else if (sortKey === 'type') {
      // Group 'Chat' values and other types apart
      const getTypeGroup = (item) => {
        const isTask = item.task_id.isNotEmpty ? item.type === 'task' : !!item.task_name;
        return isTask ? 1 : 0; // 0 for Chat, 1 for tasks
      };
      const groupA = getTypeGroup(a);
      const groupB = getTypeGroup(b);
      if (groupA !== groupB) {
        return sortOrder === 'asc' ? groupA - groupB : groupB - groupA;
      }
      // If same group, sort by type label alphabetically
      const getTypeLabel = (item) => {
        const isTask = item.task_id.isNotEmpty ? item.type === 'task' : !!item.task_name;
        return isTask ? item.task_name : 'Chat';
      };
      const typeA = (getTypeLabel(a) || '').toLowerCase();
      const typeB = (getTypeLabel(b) || '').toLowerCase();
      return sortOrder === 'asc' ? typeA.localeCompare(typeB) : typeB.localeCompare(typeA);
    } else {
      const dateA = new Date(a.timestamp);
      const dateB = new Date(b.timestamp);
      return sortOrder === 'asc' ? dateA - dateB : dateB - dateA;
    }
  }) : [];
  return (
    <div className="max-w-7xl pt-16 mx-auto px-4 sm:px-6 lg:px-8">
      <h1 className="text-2xl font-semibold mb-6">Verlauf</h1>
      <div className="overflow-hidden sm:rounded-lg">
        <div className="px-4 py-3 sm:px-6 flex justify-between text-sm font-medium text-gray-500 border-b-[2px]">
          <div className="w-2/5 cursor-pointer hover:text-gray-700 flex items-center gap-1" onClick={() => handleSort('title')}>
            <span>Titel</span>
            <div className="flex flex-col pl-2">
              <span className={`text-xs -mb-0.5 leading-none ${sortKey === 'title' && sortOrder === 'asc' ? 'text-custom-yellow' : 'text-gray-400'}`}>▲</span>
              <span className={`text-xs leading-none ${sortKey === 'title' && sortOrder === 'desc' ? 'text-custom-yellow' : 'text-gray-400'}`}>▼</span>
            </div>
          </div>
          {/* Typ column - increased width */}
          <div className="w-1/5 text-center cursor-pointer hover:text-gray-700 flex items-center justify-center gap-1" onClick={() => handleSort('type')}>
            <span>Typ</span>
            <div className="flex flex-col pl-2">
              <span className={`text-xs -mb-0.5 leading-none ${sortKey === 'type' && sortOrder === 'asc' ? 'text-custom-yellow' : 'text-gray-400'}`}>▲</span>
              <span className={`text-xs leading-none ${sortKey === 'type' && sortOrder === 'desc' ? 'text-custom-yellow' : 'text-gray-400'}`}>▼</span>
            </div>
          </div>
          <div className="w-1/5 text-center cursor-pointer hover:text-gray-700 flex items-center justify-center gap-1" onClick={() => handleSort('timestamp')}>
            <span className='text-md'>Hochgeladen am</span>
            <div className="flex flex-col pl-2 ">
              <span className={`text-xs -mb-0.5 leading-none ${sortKey === 'timestamp' && sortOrder === 'asc' ? 'text-custom-yellow' : 'text-gray-400'}`}>▲</span>
              <span className={`text-xs leading-none ${sortKey === 'timestamp' && sortOrder === 'desc' ? 'text-custom-yellow' : 'text-gray-400'}`}>▼</span>
            </div>
          </div>
          <div className="w-1/5 text-center">Status</div>
          <div className="w-1/5"></div>
        </div>
        <ul className="flex flex-col gap-2 mt-3">
          {history?.map((item, index) => {
            const { completed, total } = getCompletedQuestions(item.id);
            const isTask = item.task_id.isNotEmpty ? item.type === 'task' : !!item.task_name;
            const typeLabel = isTask ? item.task_name : 'Chat';
            return (
              <li key={index} className="px-2 rounded-lg bg-custom-white_light py-1 flex items-center justify-between hover:bg-gray-50">
                <div className="w-2/5 flex items-center">
                  <img src={constants.ai} alt="Avatar" className="w-5 h-5 rounded-full" />
                  {editingId === item.id ? (
                    <div className="flex items-center">
                      <input
                        type="text"
                        value={editingTitle}
                        onChange={handleTitleChange}
                        className="ml-2 px-2 py-1 text-sm font-medium text-gray-900 border rounded focus:outline-none focus:border-custom-yellow"
                        autoFocus
                      />
                      <button 
                        onClick={() => handleTitleSubmit(item.id)}
                        className={`ml-2 ${editingTitle !== (item.history_name || item.task_name) ? 'text-green-500' : 'text-gray-400 hover:text-gray-600'}`}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M20 6L9 17l-5-5"/>
                        </svg>
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center">
                      <span className="ml-2 text-sm font-medium text-gray-900">{item.history_name ? item.history_name : item.task_name}</span>
                      <button
                        onClick={() => handleEditClick(item.id, item.history_name || item.task_name)}
                        className="ml-2 text-gray-400 hover:text-gray-600"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"></path>
                        </svg>
                      </button>
                    </div>
                  )}
                </div>
                {/* Typ column - increased width */}
                <div className="w-1/5 text-center text-sm text-gray-700">{typeLabel}</div>
                <div className="w-1/5 text-center text-sm text-gray-500">{formatTimestamp(item.timestamp)}</div>
                {/* Status column: only show for tasks */}
                <div className="w-1/5 flex justify-center">
                  {isTask ? <CircularProgress completed={completed} total={total} /> : null}
                </div>
                <div className="w-1/5 flex justify-end space-x-4">
                  <div className='flex items-center justify-center gap-3 bg-[#EBEDF1] h-10 w-28 rounded'>
                    <img src={constants.sms} alt="Avatar" className="w-5 h-5 rounded-full" />
                    <button
                      className={`text-sm ${!isTask ? 'opacity-50 cursor-not-allowed' : ''}`}
                      onClick={() => {
                        if (isTask) {
                          // Check if user has "No License" role
                          if (userData?.user_role === 'No License') {
                            navigate('/hilfe');
                            return;
                          }
                          navigate(`/analysis/${item.id}`);
                        } else {
                          // Chat navigation is disabled
                          // navigate(`/chat/${item.id}`);
                        }
                      }}
                      disabled={!isTask} // Disable for chat items
                    >Chat öffnen</button>
                  </div>
                  <button 
                    className="text-gray-400 hover:text-gray-600" 
                    onClick={() => handleDeleteClick(item.id)}
                    disabled={deletingIds.includes(item.id)}
                  >
                    {deletingIds.includes(item.id) ? (
                      <div className="animate-spin h-5 w-5">
                        <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                      </div>
                    ) : (
                      <Trash2Icon />
                    )}
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      </div>
      <DeleteModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onDelete={handleDeleteConfirm}
        title="Chat löschen"
        message="Sind Sie sicher, dass Sie diesen Chat löschen möchten? Diese Aktion kann nicht rückgängig gemacht werden."
      />
    </div>
  );
};

export default History;